import React, { useState, useEffect } from "react";
import { X, Mail, Check, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Anonymous "Notify Me at Launch" modal.
 * Hits POST /api/launch/notify (no auth required).
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - items: optional cart items array (snapshot saved with signup)
 *  - source: "cart" | "home" | "footer" | etc
 *  - defaultEmail: optional prefilled email
 */
export default function NotifyMeModal({ open, onClose, items = [], source = "cart", defaultEmail = "", onSubscribed }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(defaultEmail || "");
      setErr("");
      setDone(false);
      setBusy(false);
    }
  }, [open, defaultEmail]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErr("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email: email.trim(),
        source,
        items: items.map((i) => ({
          product_id: i.product_id,
          variant: i.variant,
          name: i.name,
          quantity: i.quantity,
        })),
      };
      await axios.post(`${BACKEND_URL}/api/launch/notify`, payload);
      try { localStorage.setItem("psl_launch_subscribed", "1"); } catch (_) {}
      if (typeof onSubscribed === "function") onSubscribed();
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="notify-me-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-[#0C0C12] border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="notify-me-close"
          onClick={onClose}
          className="absolute top-3 right-3 text-white/50 hover:text-white p-1"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#00C3FF]/10 border border-[#00C3FF]/40 flex items-center justify-center mx-auto mb-5">
              <Check className="text-[#00C3FF]" size={24} />
            </div>
            <div className="overline mb-2">YOU'RE ON THE LIST</div>
            <h2 className="font-heading text-2xl mb-3">Check your inbox.</h2>
            <p className="text-sm text-[#8A94A6] leading-relaxed mb-6">
              We'll email <span className="text-white font-mono-tech text-xs">{email}</span> the moment
              checkout opens — and early subscribers get a launch-day discount code.
            </p>
            <button
              data-testid="notify-me-done"
              onClick={onClose}
              className="btn-ghost text-[11px]"
              style={{ padding: "10px 20px" }}
            >
              Continue Browsing
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7">
            <div className="overline mb-2">PRE-LAUNCH</div>
            <h2 className="font-heading text-2xl mb-3">Notify me at launch</h2>
            <p className="text-sm text-[#8A94A6] leading-relaxed mb-5">
              Checkout opens soon. Drop your email and we'll send you a launch-day discount the second
              we go live.
              {items.length > 0 && (
                <span className="block mt-2 text-[#00C3FF] font-mono-tech text-[11px] uppercase tracking-wider">
                  Your cart will be saved · {items.length} item{items.length !== 1 ? "s" : ""}
                </span>
              )}
            </p>

            <label className="overline block mb-2">EMAIL</label>
            <input
              data-testid="notify-me-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input-tech w-full mb-3"
              autoFocus
            />

            {err && (
              <div data-testid="notify-me-error" className="text-xs text-red-400 font-mono-tech mb-3">
                {err}
              </div>
            )}

            <button
              data-testid="notify-me-submit"
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />}
              {busy ? "Adding you..." : "Notify Me at Launch"}
            </button>

            <p className="text-[10px] text-[#5C657A] font-mono-tech text-center uppercase tracking-[0.2em] mt-4">
              No spam · Unsubscribe anytime
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
